import React, { useState } from "react";
import { setCommandCenterAccess } from "../../api/commandCenterAccessService";

/**
 * #775 — per-caregiver Command Center access switch.
 *
 * Command Center is the pro surface; this app is the light one. Both serve the
 * same caregivers, so a caregiver admin decides per person. Backend:
 * GMA-TECH/cortex-be#1009 / #1412.
 *
 * Three things worth knowing about the shape of this component:
 *
 * 1. `if (!canManage) return null` mirrors PontosenseCaregiverAccessSection.
 *    It is COSMETIC. The server authorizes every write — a caregiver admin for
 *    caregivers they created, a super admin for any caregiver — and refuses the
 *    rest with 401/400. Hiding the control is a courtesy, not a boundary.
 *
 * 2. The switch renders the value the SERVER returned, not the value clicked.
 *    On failure it goes back to where it was and says why, so the control never
 *    shows a state the database does not hold.
 *
 * 3. It does not promise instant revocation, because turning access off is not
 *    instant: the flag is a claim on the login token, so it takes effect when
 *    the caregiver's session is next re-issued. The copy says "next sign-in"
 *    rather than implying immediacy.
 *
 * English only — this app has no i18n (0 translation calls across 344 jsx
 * files), and the operator confirmed English-only on 2026-09-30. A single
 * translated control in an untranslated app would read as a defect.
 */
const CommandCenterAccessToggle = ({
  caregiverId,
  caregiverName,
  enabled,
  canManage,
  onChange,
}) => {
  // `undefined` means the roster did not carry the field, which the backend
  // does deliberately for callers who could not change it anyway. Treat it as
  // off rather than rendering an indeterminate control.
  const [isOn, setIsOn] = useState(enabled === true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!canManage) return null;

  const friendlyError = (err) => {
    const status = err?.status ?? err?.response?.status;
    if (status === 401 || status === 403) {
      return "You can only change this for caregivers you manage.";
    }
    if (status === 400) {
      return "Command Center access applies to caregivers only.";
    }
    if (status === 404) {
      return "This caregiver no longer exists. Refresh the page.";
    }
    return "Could not save that change. Please try again.";
  };

  const handleToggle = async () => {
    const previous = isOn;
    const next = !previous;

    setSaving(true);
    setError(null);
    // Optimistic, but reverted on failure — see the note above.
    setIsOn(next);

    try {
      const payload = await setCommandCenterAccess(caregiverId, next);
      // Trust the server's value over the click.
      const confirmed = payload?.data?.commandCenterEnabled === true;
      setIsOn(confirmed);
      if (onChange) onChange(confirmed);
    } catch (err) {
      setIsOn(previous);
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  const label = caregiverName
    ? `Command Center access for ${caregiverName}`
    : "Command Center access";

  return (
    <div className="command-center-access" data-testid="command-center-access">
      <label className="command-center-access__row">
        <input
          type="checkbox"
          role="switch"
          aria-label={label}
          aria-checked={isOn}
          checked={isOn}
          disabled={saving}
          aria-busy={saving}
          onChange={handleToggle}
          data-testid="command-center-access-switch"
        />
        <span className="command-center-access__title">
          Command Center access
        </span>
      </label>

      <p className="command-center-access__hint">
        {isOn
          ? "On — this caregiver can use Command Center as well as the regular CompassCare app."
          : "Off — this caregiver uses the regular CompassCare app only."}{" "}
        Changes take effect at their next sign-in.
      </p>

      {/* The error below announces itself via role="alert"; this one needs its
          own status region or a screen-reader user gets no progress signal at
          all while the switch sits disabled. `polite` rather than `assertive`
          because a save is not an interruption. */}
      {saving && (
        <p
          className="command-center-access__status"
          role="status"
          aria-live="polite"
          data-testid="command-center-access-saving"
        >
          Saving…
        </p>
      )}

      {error && (
        <p
          className="command-center-access__error"
          role="alert"
          data-testid="command-center-access-error"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default CommandCenterAccessToggle;
